import React from 'react';
import { withEditMode } from '_containers/BaseComponent';
import { convertSrcJss } from '_utils/media';
import { imagePlaceholderSrc } from '_utils/styles';
import NavLink from '../NavItem';
import { isValidLinkObject, getLink } from '_utils/helpers/link';
import { isContentful, resizeContentfulImage } from '_utils/helpers/image';
import ContentTypeAttributes from '_utils/components/Corporate/ContentTypeAttributes';
import {
  ListItemProps,
  stylesThatRenderImage,
  stylesThatRenderDate,
  stylesThatRenderDescription,
  ItemStyle
} from './definitions';
import { getContentType } from '_utils/helpers/contentType';
import Arrow from '_utils/icons/Arrow';
import External from '_utils/icons/External';
import Logo from '_utils/icons/Logo';

import {
  Card,
  Category,
  Title,
  TitleIconWrapper,
  Listing,
  ImageColumn,
  ImageContainer,
  Image as StyledImage,
  DetailsColumn,
  AttributesWrapper,
  IconContainer,
  LinkIconContainer,
  Description,
  ArrowWrapper,
  Date,
  DateDay,
  DateMonth,
  DateNumber,
  LogoWrapper
} from './StyledItem';
import PropTypes from 'prop-types';

export const componentForStyle = {
  [ItemStyle.listingWithDate]: Listing,
  [ItemStyle.listingWithImage]: Listing,
  [ItemStyle.listingWithImageAndDescription]: Listing,
  [ItemStyle.cardWithDescription]: Card,
  [ItemStyle.cardWithoutDescription]: Card
};

const LazyImage = ({ field }) => {
  if (field === null || Boolean(field?.value?.src) === false) {
    return null;
  }

  let resizeImage;

  const internal = field?.value?.src?.startsWith('/');

  if (isContentful(field.value.src)) {
    resizeImage = resizeContentfulImage(field?.value?.src, { width: 200 });
  } else if (internal) {
    resizeImage = convertSrcJss(field.value.src) + '?mw=200';
  } else {
    resizeImage = field.value.src;
  }

  return (
    <StyledImage
      className="lazyload"
      field={{
        value: {
          src: imagePlaceholderSrc,
          alt: field.value?.alt,
          'data-src': resizeImage
        }
      }}
    />
  );
};

LazyImage.propTypes = {
  field: PropTypes.shape({
    value: PropTypes.shape({
      src: PropTypes.string,
      alt: PropTypes.string,
    }),
  }),
};

const ImageRenderer = ({ field, editMode }) => {
  if (editMode) {
    return <StyledImage field={field} />;
  }

  return <LazyImage field={field} />;
};

ImageRenderer.propTypes = {
  field: PropTypes.shape({
    value: PropTypes.shape({
      src: PropTypes.string,
      alt: PropTypes.string,
    }),
  }),
  editMode: PropTypes.bool,
};

const Image = withEditMode(ImageRenderer);

const Item: React.FC<ListItemProps> = ({
  type,
  title,
  description,
  primaryCategory,
  image,
  itemStyle,
  link,
  attributes,
  dateDay,
  dateNumber,
  dateMonth,
  editMode
}) => {
  const descriptionForLabel: string = (description as string) || '';
  const renderImage = stylesThatRenderImage[itemStyle] || false;
  const renderDate = stylesThatRenderDate[itemStyle] || false;
  const renderDescription = stylesThatRenderDescription[itemStyle] || false;
  const contentType = getContentType(type);
  const Component = componentForStyle[itemStyle] || Card;
  const renderPlaceholder = renderImage && !image?.value?.src;

  // convert plain strings into a field-like object
  if (typeof description === 'string') {
    description = {
      value: description
    };
  }

  let linkObj: any = getLink(link);

  const linkProps = {
    link: linkObj
  };

  const wrapperIsLink = Component === Card;
  const titleIsLink = Component !== Card;

  const Wrapper: React.ComponentType<any> = wrapperIsLink ? NavLink : Component;
  const wrapperProps = wrapperIsLink
    ? {
      ...linkProps,
      StyledLink: Component
    }
    : {};

  let titleWrapperProps = {
    link: null,
    className: 'title-is-link',
    StyledLink: titleIsLink ? LinkIconContainer : IconContainer
  };

  if (titleIsLink) {
    titleWrapperProps = {
      ...titleWrapperProps,
      ...linkProps
    };
  }

  return (
    <Wrapper className={contentType} {...wrapperProps}>
      {renderDate && (
        <ImageColumn>
          <ImageContainer>
            <Date>
              {dateDay && <DateDay>{dateDay}</DateDay>}
              {dateNumber && <DateNumber>{dateNumber}</DateNumber>}
              {dateMonth && <DateMonth>{dateMonth}</DateMonth>}
            </Date>
          </ImageContainer>
        </ImageColumn>
      )}
      {renderImage && (
        <ImageColumn>
          <ImageContainer>
            <Image field={image} /* {...imageProps} */ />
            {renderPlaceholder && (
              <LogoWrapper aria-hidden={true}>
                <Logo fill="#fff" />
              </LogoWrapper>
            )}
          </ImageContainer>
        </ImageColumn>
      )}
      <DetailsColumn>
        {primaryCategory && <Category>{primaryCategory}</Category>}
        {title && (
          <NavLink {...titleWrapperProps} description={descriptionForLabel}>
            <Title>{title}</Title>
            {isValidLinkObject(linkObj) && (
              <TitleIconWrapper>
                &#65279;
                <ArrowWrapper>
                  {linkObj.type !== 'external' && <Arrow ariaHidden={true} />}
                  {linkObj.type === 'external' && <External ariaHidden={true} />}
                </ArrowWrapper>
              </TitleIconWrapper>
            )}
          </NavLink>
        )}
        {renderDescription && <Description field={description} />}

        <ContentTypeAttributes
          attributes={attributes}
          contentType={contentType}
          Wrapper={AttributesWrapper}
        />
      </DetailsColumn>
    </Wrapper>
  );
};



export default Item;
